import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { paths } from '@app/constants';
import { usePermissions, useTokenUserData } from '@data/hooks';
import { TrialDetailsView, MatchingTrialStatus } from '@data/sdk/match';
import { useOnboardingsStore, useVisitedTrialsStore } from '@data/storage';
import { getUserTeamPermissions } from '@data/utils/helpers';

export const useTrialRedirect = (
  trial: TrialDetailsView | undefined,
  isSingleTeamMember: boolean,
) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { userId } = useTokenUserData();
  const { onboardings = {} } = useOnboardingsStore();
  const { SEE_FUNNEL, IS_ADMIN, MANAGE_TRIAL } = usePermissions();
  const { trialId = '' } = useParams<{ trialId: string }>();

  const params = new URLSearchParams(window.location.search);
  const fromTrialLibrary = params.get('fromTrialLibrary');

  const { setVisitedTrials, visitedTrials } = useVisitedTrialsStore();

  const { IS_PATIENT_ORG } = getUserTeamPermissions();

  const onboardingCompleted =
    ((onboardings[userId] && onboardings[userId]?.trials[trialId]?.completed) ||
      !onboardings[userId]) ??
    false;

  const isFirstVisit = !visitedTrials.includes(trial?.id ?? '');
  const hasNecessaryPermissionsToSeeFunnel =
    trial?.matchingStatus === MatchingTrialStatus.Active && SEE_FUNNEL && !IS_PATIENT_ORG;

  const shouldRedirectToFunnel =
    (hasNecessaryPermissionsToSeeFunnel &&
      !IS_ADMIN &&
      ((isSingleTeamMember && !isFirstVisit && onboardingCompleted) ||
        (isSingleTeamMember && trial?.isValidated && onboardingCompleted) ||
        (!isSingleTeamMember && trial?.isValidated) ||
        (!MANAGE_TRIAL && onboardingCompleted))) ||
    fromTrialLibrary;

  const shouldMarkAsVisited =
    hasNecessaryPermissionsToSeeFunnel && isSingleTeamMember && isFirstVisit && !IS_ADMIN;

  useEffect(() => {
    if (shouldMarkAsVisited) {
      setVisitedTrials(trial?.id ?? '');
    }
    navigate(
      // eslint-disable-next-line no-nested-ternary
      shouldRedirectToFunnel
        ? paths.volunteers
        : onboardingCompleted
        ? paths.criterias
        : paths.onboarding.trial_overview,
      { replace: true, state },
    );
  }, []);

  return {
    trial,
  };
};
